import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { notifyError } from "src/assets/toastService";
import { Order, OrderedProduct, Product } from "src/shared/services/interfacev2";

const getOid = (id: any): string =>
  typeof id === "string" ? id : (id?.$oid ?? "");

const mapApiProductToOrderedProduct = (
  item: any,
  catalogProduct?: Product
): OrderedProduct => {
  const unitPrice = Number(item.unitPrice ?? 0);
  const quantity = Number(item.quantity ?? 1);

  return {
    productId: item.productId,
    name: item.name,
    arabicName: item.arabicName ?? "",
    quantity,
    unitPrice,
    lineTotal: unitPrice * quantity,
    type: catalogProduct?.type ?? "",
    notes: item.notes || "",
  };
};

interface UseEditOrderHandlerProps {
  products: Product[];
  setSelectedProducts: (products: OrderedProduct[]) => void;
  setDiscount: (discount: number) => void;
  setIsEditing: (editing: boolean) => void;
  // Updated to use separate name fields
  setFirstName: (name: string) => void;
  setMiddleName: (name: string) => void;
  setLastName: (name: string) => void;
  setPhoneNumber: (phone: string) => void;
  setTableNumber: (table: string) => void;
  setGuess: (guess: string) => void;
  setOrderType: (type: string) => void;
  setDeliveryFee: (fee: number) => void;
  setParcelFee: (fee: number) => void;
}

export const useEditOrderHandler = ({
  products,
  setSelectedProducts,
  setDiscount,
  setIsEditing,
  setFirstName,
  setMiddleName,
  setLastName,
  setPhoneNumber,
  setTableNumber,
  setGuess,
  setOrderType,
  setDeliveryFee,
  setParcelFee,
}: UseEditOrderHandlerProps) => {
  const location = useLocation();

  const [oldProducts, setOldProducts] = useState<Order>();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  useEffect(() => {
    const editOrder: Order | undefined = location.state?.editOrder;

    // wait until products loaded
    if (!editOrder || !products || products.length === 0) return;

    // prevent rerun
    if (oldProducts) return;

    const loadEditOrder = () => {
      setIsLoadingOrder(true);

      try {
        window.history.replaceState({}, document.title);

        setOldProducts(editOrder);
        setIsEditing(true);

        /* ---------------- CUSTOMER ---------------- */
        // Set individual name fields
        setFirstName(editOrder.customerDetails?.firstName ?? "");
        setMiddleName(editOrder.customerDetails?.middleName ?? "");
        setLastName(editOrder.customerDetails?.lastName ?? "");

        // Set phone number - handle both array and single string formats
        const phone = editOrder.customerDetails?.phones?.[0]?.phone ??  editOrder.customerDetails?.phones?.[0] ?? "";
        setPhoneNumber(String(phone));

        /* ---------------- ORDER CORE ---------------- */
        const orderCore = editOrder.orders?.[0];
        const orderDetails = editOrder.orderDetails?.[0];

        setDiscount(Number(orderCore?.discount ?? 0));

        setTableNumber(String(orderCore?.tableNumber ?? ""));

        setGuess(String(orderCore?.guest ?? ""));

        // ✅ order type
        const orderType = orderDetails?.orderType ?? "Table";
        setOrderType(orderType);

        // ✅ delivery fee
        const deliveryFee = Number(orderCore?.deliveryFee ?? 0);
        setDeliveryFee(deliveryFee);

        // ✅ parcel fee
        const parcelFee = Number(orderCore?.parcelFee ?? 0);
        setParcelFee(parcelFee);

        /* ---------------- PRODUCTS ---------------- */
        const apiProducts = orderDetails?.products ?? [];

        const orderedProducts: OrderedProduct[] = apiProducts.map(
          (item: any) => {
            const catalogProduct = products?.find(
              (p) => getOid(p._id) === item.productId
            );

            return mapApiProductToOrderedProduct(
              item,
              catalogProduct
            );
          }
        );

        setSelectedProducts(orderedProducts);
      } catch (error) {
        console.error("Edit order load failed:", error);
        notifyError("Failed to load order for editing");
      } finally {
        setIsLoadingOrder(false);
      }
    };

    loadEditOrder();
  }, [location.state, products]);

  return {
    oldProducts,
    setOldProducts,
    isLoadingOrder,
  };
};